import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { isNotNil } from 'ramda'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  ANCCAccreditation,
  CAPCEAccreditationV2,
  Event,
  EventCompliance,
  Instructor,
  LifecycleState,
  QuestionData,
  QuizV2,
  Syllabus,
  TimeStamps,
} from 'src/v3/core/types'
import {
  archiveEvent as archiveEventAPI,
  preArchiveEvent as preArchiveEventAPI,
  reactivateEvent as reactivateEventAPI,
  restoreEvent as restoreEventAPI,
} from './utils/event-lifecycle-api'

// ─── Local types ─────────────────────────────────────────────────────────────

type PlanningEvent = {
  id: string
  linkedServiceId?: string
  banner?: string
  thumbnail?: string
  name: string
  description: string
  hours: number
  category: string | { other: string }
  attendanceTrackingMechanism:
    | {
        quizPasscode?: string
      }
    | 'ManagerTracked'
    | 'QuizPasscode'
  eventType:
    | {
        virtual?: {
          name: string
          url: string
        }
      }
    | {
        case: 'Virtual' | 'InPerson' | 'Hybrid'
        fields: {
          name?: string
          url?: string
          address?: string
          city?: string
          state?: string
          zip?: string
        }
      }
  featured: boolean
  lifecycleState?: LifecycleState
} & TimeStamps

type PlanningEventProgress = {
  eventID: string
  eventName: string
  dataCompleteness: {
    fieldsCompleted: number
    fieldsTotal: number
    percentComplete: number
    missingFields: string[]
  }
  complianceStatus: {
    approved: number
    pending: number
    rejected: number
    notSubmitted: number
    percentApproved: number
    rejectedFields: string[]
    pendingFields: string[]
  }
  lastUpdated: string | null
}

type PlanningEventProgressResponse = {
  events: PlanningEventProgress[]
  summary: {
    total: number
    byCompleteness: {
      zeroTo25: number
      twentyFiveTo50: number
      fiftyTo75: number
      seventyFiveTo100: number
    }
    byCompliance: {
      notReady: number
      readyForReview: number
      inReview: number
      needsRevision: number
      approved: number
    }
  }
}

type EventDetails = {
  quiz?: QuizV2 & { questions: QuestionData[] }
  instructors?: Instructor[]
  template?: {
    id: string
    linkedServiceID?: string
    name: string
    statement1: string
    statement2: string
    createdTime?: string
    updatedTime?: string
  }
  anccAccreditation?: ANCCAccreditation
  capceAccreditation?: CAPCEAccreditationV2
  syllabus?: Syllabus
}

// ─── State ───────────────────────────────────────────────────────────────────

type EventPipelineState = {
  events: Event[]
  eventsStatus: Status
  eventDetails: Record<string, EventDetails>
  eventDetailsStatus: Record<string, Status>
  eventCompliance: Record<string, EventCompliance>
  eventComplianceStatus: Status
  planningEvents: PlanningEvent[]
  planningEventsStatus: Status
  planningEventProgress: Record<string, PlanningEventProgress>
  planningEventProgressStatus: Status
  planningEventProgressSummary: {
    total: number
    byCompleteness: {
      zeroTo25: number
      twentyFiveTo50: number
      fiftyTo75: number
      seventyFiveTo100: number
    }
    byCompliance: {
      notReady: number
      readyForReview: number
      inReview: number
      needsRevision: number
      approved: number
    }
  } | null
  syllabi: Record<string, Syllabus>
  syllabiStatus: Record<string, Status>
  syllabusGenerations: Record<
    string,
    {
      correlationID: string
      status: 'SessionInProgress' | 'SessionCompleted' | 'SessionFailed'
      result?: {
        text: string
        practiceGap: string
        practiceGapEvidence: string
        learnerEngagement: string
        learningOutcomes: string
      }
    }
  >
  capceAccreditations: Record<string, CAPCEAccreditationV2>
  capceAccreditationsStatus: Record<string, Status>
  anccAccreditations: Record<string, ANCCAccreditation>
  anccAccreditationsStatus: Record<string, Status>
  references: Record<
    string,
    {
      id: string
      text: string
      linkedServiceID: string | null
      createdTime: string
      updatedTime: string
    }[]
  >
  referencesStatus: Record<string, Status>
  objectives: Record<
    string,
    {
      id: string
      syllabusID: string
      text: string
      sequence: number
      createdTime: string | null
      updatedTime: string | null
    }[]
  >
  objectivesStatus: Record<string, Status>
  templates: Record<
    string,
    {
      id: string
      linkedServiceID?: string
      name: string
      statement1: string
      statement2: string
      createdTime?: string
      updatedTime?: string
    }
  >
  templatesStatus: Record<string, Status>
  instructors: Record<string, Instructor[]>
  instructorsStatus: Record<string, Status>
  allEventInstructorsStatus: Status
  availableInstructors: Instructor[]
  availableInstructorsStatus: Status
  quizzes: Record<string, QuizV2>
  quizzesStatus: Record<string, Status>
  quizQuestions: Record<string, QuestionData[]>
  quizQuestionsStatus: Record<string, Status>
  eventLifecycleOperationStatus: Status
  eventLifecycleOperationError: string | null
  eventLifecycleOperationSuccess: string | null
}

let initialState: EventPipelineState = {
  events: [],
  eventsStatus: 'uninitialized',
  eventDetails: {},
  eventDetailsStatus: {},
  eventCompliance: {},
  eventComplianceStatus: 'uninitialized',
  planningEvents: [],
  planningEventsStatus: 'uninitialized',
  planningEventProgress: {},
  planningEventProgressStatus: 'uninitialized',
  planningEventProgressSummary: null,
  syllabi: {},
  syllabiStatus: {},
  syllabusGenerations: {},
  capceAccreditations: {},
  capceAccreditationsStatus: {},
  anccAccreditations: {},
  anccAccreditationsStatus: {},
  references: {},
  referencesStatus: {},
  objectives: {},
  objectivesStatus: {},
  templates: {},
  templatesStatus: {},
  instructors: {},
  instructorsStatus: {},
  allEventInstructorsStatus: 'uninitialized',
  availableInstructors: [],
  availableInstructorsStatus: 'uninitialized',
  quizzes: {},
  quizzesStatus: {},
  quizQuestions: {},
  quizQuestionsStatus: {},
  eventLifecycleOperationStatus: 'uninitialized',
  eventLifecycleOperationError: null,
  eventLifecycleOperationSuccess: null,
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export let fetchEvents = createAsyncThunk('eventPipeline/fetchEvents', () =>
  axios
    .get<any[]>('/api/pipeline/events')
    .then(pluckData)
    .then((data) =>
      data.map((event): Event => {
        let [eventType, location] = Object.entries<any>(event.eventType)[0]
        let flattenedLocation =
          eventType === 'hybrid' &&
          Array.isArray(location) &&
          location.length === 2
            ? { ...location[1], ...location[0], virtualName: location[1].name }
            : location
        return {
          ...event,
          attendanceTrackingMechanism: {
            kind: isNotNil(event.attendanceTrackingMechanism.quizPasscode)
              ? 'quizPasscode'
              : 'managerTracked',
            quizPasscode: event.attendanceTrackingMechanism.quizPasscode,
          },
          category: {
            kind: event.category.other ? 'other' : event.category,
            other: event.category.other,
          },
          categoryName: isNotNil(event.category.other)
            ? 'Other'
            : {
                'airway|Respiratory|Ventilation':
                  'Airway|Respiratory|Ventilation',
                medical: 'Medical',
                trauma: 'Trauma',
                operations: 'Operations',
                'critical Care': 'Critical Care',
                cardiovascular: 'Cardiovascular',
                pediatrics: 'Pediatrics',
              }[event.category as string],
          location: {
            kind: eventType,
            ...flattenedLocation,
          },
          eventType:
            {
              virtual: 'Virtual',
              inPerson: 'In Person',
              hybrid: 'Hybrid',
            }[eventType] || 'Unknown',
        }
      }),
    ),
)

export let fetchEventDetails = createAsyncThunk(
  'eventPipeline/fetchEventDetails',
  (eventID: string) =>
    axios
      .get<EventDetails>(`/api/pipeline/event-details?eventID=${eventID}`)
      .then(pluckData),
)

export let fetchEventCompliances = createAsyncThunk(
  'eventPipeline/fetchEventCompliances',
  () =>
    axios
      .get<EventCompliance[]>('/api/pipeline/event-compliances')
      .then(pluckData)
      .then((data) =>
        data.reduce((obj, ec) => ({ ...obj, [ec.eventID]: ec }), {}),
      ),
)

export let fetchPlanningEvents = createAsyncThunk(
  'eventPipeline/fetchPlanningEvents',
  () =>
    axios.get<PlanningEvent[]>('/api/pipeline/planning/events').then(pluckData),
)

export let fetchPlanningEventsProgress = createAsyncThunk(
  'eventPipeline/fetchPlanningEventsProgress',
  () =>
    axios
      .get<PlanningEventProgressResponse>(
        '/api/pipeline/planning/events/progress',
      )
      .then(pluckData),
)

export let preArchiveEvent = createAsyncThunk(
  'eventPipeline/preArchiveEvent',
  (eventId: string) =>
    preArchiveEventAPI(eventId).then((data) => ({ ...data, eventId })),
)

export let reactivateEvent = createAsyncThunk(
  'eventPipeline/reactivateEvent',
  (eventId: string) =>
    reactivateEventAPI(eventId).then((data) => ({ ...data, eventId })),
)

export let archiveEvent = createAsyncThunk(
  'eventPipeline/archiveEvent',
  (eventId: string) =>
    archiveEventAPI(eventId).then((data) => ({ ...data, eventId })),
)

export let restoreEvent = createAsyncThunk(
  'eventPipeline/restoreEvent',
  (eventId: string) =>
    restoreEventAPI(eventId).then((data) => ({ ...data, eventId })),
)

export let deletePlanningEvent = createAsyncThunk(
  'eventPipeline/deletePlanningEvent',
  (eventId: string) =>
    axios
      .delete(`/api/pipeline/compliance-event/${eventId}`)
      .then(() => ({ eventId })),
)

export let createPlanningEvent = createAsyncThunk(
  'eventPipeline/createPlanningEvent',
  (eventData: Partial<PlanningEvent>) =>
    axios
      .post<PlanningEvent>('/api/pipeline/planning/events', eventData)
      .then(pluckData),
)

export let updatePlanningEvent = createAsyncThunk(
  'eventPipeline/updatePlanningEvent',
  (eventData: PlanningEvent) =>
    axios
      .put<PlanningEvent>('/api/pipeline/planning/events', eventData)
      .then(pluckData),
)

export let updatePlanningEventField = createAsyncThunk(
  'eventPipeline/updatePlanningEventField',
  ({
    eventId,
    fieldUpdates,
  }: {
    eventId: string
    fieldUpdates: Partial<PlanningEvent>
  }) =>
    axios
      .put<PlanningEvent>('/api/pipeline/planning/events', {
        id: eventId,
        ...fieldUpdates,
      })
      .then(pluckData),
)

export let fetchSyllabus = createAsyncThunk(
  'eventPipeline/fetchSyllabus',
  (eventID: string) =>
    axios
      .get<Syllabus>(`/api/pipeline/planning/events/${eventID}/syllabus`)
      .then(pluckData),
)

export let createSyllabus = createAsyncThunk(
  'eventPipeline/createSyllabus',
  (syllabusData: {
    eventID: string
    text?: string
    practiceGap?: string
    practiceGapEvidence?: string
    learnerEngagement?: string
    learningOutcomes?: string
  }) =>
    axios
      .post<Syllabus>('/api/pipeline/planning/events/syllabus', syllabusData)
      .then(pluckData),
)

export let updateSyllabus = createAsyncThunk(
  'eventPipeline/updateSyllabus',
  (syllabusData: {
    id: string
    text?: string
    practiceGap?: string
    practiceGapEvidence?: string
    learnerEngagement?: string
    learningOutcomes?: string
  }) =>
    axios
      .put<Syllabus>('/api/pipeline/planning/events/syllabus', syllabusData)
      .then(pluckData),
)

export let generateSyllabusWithAI = createAsyncThunk(
  'eventPipeline/generateSyllabusWithAI',
  ({ eventID, prompt }: { eventID: string; prompt: string }) =>
    axios
      .post<{ correlationID: string }>(
        '/api/pipeline/planning/syllabus/generate',
        {
          eventID,
          prompt,
        },
      )
      .then(pluckData),
)

export let checkSyllabusGenerationStatus = createAsyncThunk(
  'eventPipeline/checkSyllabusGenerationStatus',
  (correlationID: string) =>
    axios
      .get<{
        correlationID: string
        status: 'SessionInProgress' | 'SessionCompleted' | 'SessionFailed'
        createdAt: string
        updatedAt: string
        progress: any[]
        result: {
          text: string
          practiceGap: string
          practiceGapEvidence: string
          learnerEngagement: string
          learningOutcomes: string
        } | null
      }>(`/api/pipeline/planning/syllabus/generate/${correlationID}/status`)
      .then(pluckData),
)

export let fetchCAPCEAccreditation = createAsyncThunk(
  'eventPipeline/fetchCAPCEAccreditation',
  (eventID: string) =>
    axios
      .get<CAPCEAccreditationV2>(
        `/api/pipeline/planning/events/${eventID}/capce`,
      )
      .then(pluckData),
)

export let createCAPCEAccreditation = createAsyncThunk(
  'eventPipeline/createCAPCEAccreditation',
  (accreditationData: {
    eventID: string
    courseCode: string
    expirationDate: string | null
    applicationDate: string | null
    submissionDate: string | null
    topicArea: string
    classType: string
    allClearDate?: string | null
    goLiveDate?: string | null
  }) =>
    axios
      .post<CAPCEAccreditationV2>(
        '/api/pipeline/planning/events/capce',
        accreditationData,
      )
      .then(pluckData),
)

export let updateCAPCEAccreditation = createAsyncThunk(
  'eventPipeline/updateCAPCEAccreditation',
  (accreditationData: {
    id: string
    courseCode?: string
    expirationDate?: string
    applicationDate?: string
    submissionDate?: string
    topicArea?: string
    classType?: string
    allClearDate?: string | null
    goLiveDate?: string | null
  }) =>
    axios
      .put<CAPCEAccreditationV2>(
        '/api/pipeline/planning/events/capce',
        accreditationData,
      )
      .then(pluckData),
)

export let fetchANCCAccreditation = createAsyncThunk(
  'eventPipeline/fetchANCCAccreditation',
  (eventID: string) =>
    axios
      .get<ANCCAccreditation>(`/api/pipeline/planning/events/${eventID}/ancc`)
      .then(pluckData),
)

export let createANCCAccreditation = createAsyncThunk(
  'eventPipeline/createANCCAccreditation',
  (accreditationData: {
    eventID: string
    courseCode: string
    expirationDate: string
    targetAudience: string[]
    educationalNeeds: string[]
    competency?: string
    professionalSource?: string
  }) =>
    axios
      .post<ANCCAccreditation>(
        '/api/pipeline/planning/events/ancc',
        accreditationData,
      )
      .then(pluckData),
)

export let updateANCCAccreditation = createAsyncThunk(
  'eventPipeline/updateANCCAccreditation',
  (accreditationData: {
    id: string
    courseCode?: string
    expirationDate?: string
    targetAudience?: string[]
    educationalNeeds?: string[]
    competency?: string
    professionalSource?: string
  }) =>
    axios
      .put<ANCCAccreditation>(
        '/api/pipeline/planning/events/ancc',
        accreditationData,
      )
      .then(pluckData),
)

export let fetchReferences = createAsyncThunk(
  'eventPipeline/fetchReferences',
  (eventID: string) =>
    axios
      .get<
        {
          id: string
          text: string
          linkedServiceID: string | null
          createdTime: string
          updatedTime: string
        }[]
      >(`/api/pipeline/planning/events/${eventID}/references`)
      .then(pluckData),
)

export let createReferences = createAsyncThunk(
  'eventPipeline/createReferences',
  (referencesData: { eventID: string; references: { text: string }[] }) =>
    axios
      .post<
        {
          id: string
          text: string
          linkedServiceID: string | null
          createdTime: string
          updatedTime: string
        }[]
      >('/api/pipeline/planning/events/references', referencesData)
      .then(pluckData),
)

export let updateReferences = createAsyncThunk(
  'eventPipeline/updateReferences',
  (referencesData: { eventID: string; references: { text: string }[] }) =>
    axios
      .put<
        {
          id: string
          text: string
          linkedServiceID: string | null
          createdTime: string
          updatedTime: string
        }[]
      >('/api/pipeline/planning/events/references', referencesData)
      .then(pluckData),
)

export let fetchObjectives = createAsyncThunk(
  'eventPipeline/fetchObjectives',
  (eventID: string) =>
    axios
      .get<
        {
          id: string
          syllabusID: string
          text: string
          sequence: number
          createdTime: string | null
          updatedTime: string | null
        }[]
      >(`/api/pipeline/planning/events/${eventID}/objectives`)
      .then(pluckData),
)

export let createObjectives = createAsyncThunk(
  'eventPipeline/createObjectives',
  (objectivesData: { eventID: string; objectives: { text: string }[] }) =>
    axios
      .post<
        {
          id: string
          syllabusID: string
          text: string
          sequence: number
          createdTime: string | null
          updatedTime: string | null
        }[]
      >('/api/pipeline/planning/events/objectives', objectivesData)
      .then(pluckData),
)

export let updateObjectives = createAsyncThunk(
  'eventPipeline/updateObjectives',
  (objectivesData: { eventID: string; objectives: { text: string }[] }) =>
    axios
      .put<
        {
          id: string
          syllabusID: string
          text: string
          sequence: number
          createdTime: string | null
          updatedTime: string | null
        }[]
      >('/api/pipeline/planning/events/objectives', objectivesData)
      .then(pluckData),
)

export let fetchTemplate = createAsyncThunk(
  'eventPipeline/fetchTemplate',
  (eventID: string) =>
    axios
      .get<{
        id: string
        linkedServiceID?: string
        name: string
        statement1: string
        statement2: string
        createdTime?: string
        updatedTime?: string
      }>(`/api/pipeline/planning/events/${eventID}/template`)
      .then(pluckData),
)

export let assignTemplate = createAsyncThunk(
  'eventPipeline/assignTemplate',
  (templateData: { eventID: string; templateID: string }) =>
    axios
      .post<{
        id: string
        linkedServiceID?: string
        name: string
        statement1: string
        statement2: string
        createdTime?: string
        updatedTime?: string
      }>('/api/pipeline/planning/events/template', templateData)
      .then(pluckData),
)

export let updateTemplateAssignment = createAsyncThunk(
  'eventPipeline/updateTemplateAssignment',
  (templateData: { eventID: string; templateID: string }) =>
    axios
      .put<{
        id: string
        linkedServiceID?: string
        name: string
        statement1: string
        statement2: string
        createdTime?: string
        updatedTime?: string
      }>('/api/pipeline/planning/events/template', templateData)
      .then(pluckData),
)

export let fetchInstructors = createAsyncThunk(
  'eventPipeline/fetchInstructors',
  (eventID: string) =>
    axios
      .get<Instructor[]>(`/api/pipeline/planning/events/${eventID}/instructors`)
      .then(pluckData),
)

export let fetchAllEventInstructors = createAsyncThunk(
  'eventPipeline/fetchAllEventInstructors',
  () =>
    axios
      .get<{ eventId: string; instructors: Instructor[] }[]>(
        '/api/pipeline/planning/events/all-instructors',
      )
      .then(pluckData),
)

export let assignInstructors = createAsyncThunk(
  'eventPipeline/assignInstructors',
  (instructorData: {
    eventID: string
    instructors: { instructorID: string }[]
  }) =>
    axios
      .post<Instructor[]>(
        '/api/pipeline/planning/events/instructors',
        instructorData,
      )
      .then(pluckData),
)

export let updateInstructorAssignment = createAsyncThunk(
  'eventPipeline/updateInstructorAssignment',
  (instructorData: {
    eventID: string
    instructors: { instructorID: string }[]
  }) =>
    axios
      .put<Instructor[]>(
        '/api/pipeline/planning/events/instructors',
        instructorData,
      )
      .then(pluckData),
)

export let fetchAvailableInstructors = createAsyncThunk(
  'eventPipeline/fetchAvailableInstructors',
  () =>
    axios
      .get<Instructor[]>('/api/pipeline/planning/events/instructors')
      .then(pluckData),
)

export let createInstructor = createAsyncThunk(
  'eventPipeline/createInstructor',
  (payload: { name: string; credentials?: string | null }) =>
    axios
      .post<Instructor>('/api/pipeline/planning/instructor', payload)
      .then(pluckData),
)

export let updateInstructor = createAsyncThunk(
  'eventPipeline/updateInstructor',
  (args: {
    instructorId: string
    payload: { name: string; credentials?: string | null }
  }) =>
    axios
      .put<Instructor>(
        `/api/pipeline/planning/instructor/${args.instructorId}`,
        args.payload,
      )
      .then(pluckData),
)

export let fetchQuiz = createAsyncThunk(
  'eventPipeline/fetchQuiz',
  (eventID: string) =>
    axios
      .get<QuizV2>(`/api/pipeline/planning/events/${eventID}/quiz`)
      .then(pluckData),
)

export let createQuiz = createAsyncThunk(
  'eventPipeline/createQuiz',
  (quizData: {
    eventID: string
    passingScore: number
    displayedQuestions: number
    randomizeQuestions: boolean
  }) =>
    axios
      .post<QuizV2>('/api/pipeline/planning/events/quiz', quizData)
      .then(pluckData),
)

export let fetchQuizQuestions = createAsyncThunk(
  'eventPipeline/fetchQuizQuestions',
  (eventID: string) =>
    axios
      .get<QuestionData[]>(
        `/api/pipeline/planning/events/${eventID}/quiz/questions`,
      )
      .then(pluckData),
)

// ─── Helpers ─────────────────────────────────────────────────────────────────

let applyLifecycleTransition = (
  state: EventPipelineState,
  eventId: string,
  toState: LifecycleState,
) => {
  let event = state.events.find((e) => e.id === eventId)
  if (event) {
    event.lifecycleState = toState
  }
  let planningEvent = state.planningEvents.find((e) => e.id === eventId)
  if (planningEvent) {
    planningEvent.lifecycleState = toState
  }
}

// ─── Slice ────────────────────────────────────────────────────────────────────

let eventPipelineSlice = createSlice({
  name: 'eventPipeline',
  initialState,
  reducers: {
    clearEventLifecycleOperationStatus: (state) => {
      state.eventLifecycleOperationStatus = 'uninitialized'
      state.eventLifecycleOperationError = null
      state.eventLifecycleOperationSuccess = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.eventsStatus = 'pending'
      })
      .addCase(fetchEvents.rejected, (state) => {
        state.eventsStatus = 'rejected'
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.eventsStatus = 'fulfilled'
        state.events = action.payload
      })
      .addCase(fetchEventDetails.pending, (state, action) => {
        state.eventDetailsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchEventDetails.rejected, (state, action) => {
        state.eventDetailsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchEventDetails.fulfilled, (state, action) => {
        state.eventDetailsStatus[action.meta.arg] = 'fulfilled'
        state.eventDetails[action.meta.arg] = action.payload
      })
      .addCase(fetchEventCompliances.pending, (state) => {
        state.eventComplianceStatus = 'pending'
      })
      .addCase(fetchEventCompliances.rejected, (state) => {
        state.eventComplianceStatus = 'rejected'
      })
      .addCase(fetchEventCompliances.fulfilled, (state, action) => {
        state.eventComplianceStatus = 'fulfilled'
        state.eventCompliance = action.payload
      })
      .addCase(fetchPlanningEvents.pending, (state) => {
        state.planningEventsStatus = 'pending'
      })
      .addCase(fetchPlanningEvents.rejected, (state) => {
        state.planningEventsStatus = 'rejected'
      })
      .addCase(fetchPlanningEvents.fulfilled, (state, action) => {
        state.planningEventsStatus = 'fulfilled'
        state.planningEvents = action.payload
      })
      .addCase(fetchPlanningEventsProgress.pending, (state) => {
        state.planningEventProgressStatus = 'pending'
      })
      .addCase(fetchPlanningEventsProgress.rejected, (state) => {
        state.planningEventProgressStatus = 'rejected'
      })
      .addCase(fetchPlanningEventsProgress.fulfilled, (state, action) => {
        state.planningEventProgressStatus = 'fulfilled'
        state.planningEventProgressSummary = action.payload.summary
        action.payload.events.forEach((progress) => {
          state.planningEventProgress[progress.eventID] = progress
        })
      })
      .addCase(createPlanningEvent.pending, (state) => {
        state.planningEventsStatus = 'pending'
      })
      .addCase(createPlanningEvent.rejected, (state) => {
        state.planningEventsStatus = 'rejected'
      })
      .addCase(createPlanningEvent.fulfilled, (state, action) => {
        state.planningEventsStatus = 'fulfilled'
        state.planningEvents = [...state.planningEvents, action.payload]
      })
      .addCase(updatePlanningEvent.pending, (state) => {
        state.planningEventsStatus = 'pending'
      })
      .addCase(updatePlanningEvent.rejected, (state) => {
        state.planningEventsStatus = 'rejected'
      })
      .addCase(updatePlanningEvent.fulfilled, (state, action) => {
        state.planningEventsStatus = 'fulfilled'
        let index = state.planningEvents.findIndex(
          (event) => event.id === action.payload.id,
        )
        if (index !== -1) {
          state.planningEvents[index] = action.payload
        }
      })
      .addCase(updatePlanningEventField.pending, (state) => {
        state.planningEventsStatus = 'pending'
      })
      .addCase(updatePlanningEventField.rejected, (state) => {
        state.planningEventsStatus = 'rejected'
      })
      .addCase(updatePlanningEventField.fulfilled, (state, action) => {
        state.planningEventsStatus = 'fulfilled'
        let index = state.planningEvents.findIndex(
          (event) => event.id === action.payload.id,
        )
        if (index !== -1) {
          state.planningEvents[index] = action.payload
        }
      })
      // Syllabus reducers
      .addCase(fetchSyllabus.pending, (state, action) => {
        state.syllabiStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchSyllabus.rejected, (state, action) => {
        state.syllabiStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchSyllabus.fulfilled, (state, action) => {
        state.syllabiStatus[action.meta.arg] = 'fulfilled'
        state.syllabi[action.meta.arg] = action.payload
      })
      .addCase(createSyllabus.pending, (state, action) => {
        state.syllabiStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(createSyllabus.rejected, (state, action) => {
        state.syllabiStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(createSyllabus.fulfilled, (state, action) => {
        state.syllabiStatus[action.meta.arg.eventID] = 'fulfilled'
        state.syllabi[action.meta.arg.eventID] = action.payload
      })
      .addCase(updateSyllabus.pending, (state, action) => {
        let eventID = Object.keys(state.syllabi).find(
          (key) => state.syllabi[key].id === action.meta.arg.id,
        )
        if (eventID) {
          state.syllabiStatus[eventID] = 'pending'
        }
      })
      .addCase(updateSyllabus.rejected, (state, action) => {
        let eventID = Object.keys(state.syllabi).find(
          (key) => state.syllabi[key].id === action.meta.arg.id,
        )
        if (eventID) {
          state.syllabiStatus[eventID] = 'rejected'
        }
      })
      .addCase(updateSyllabus.fulfilled, (state, action) => {
        let eventID = Object.keys(state.syllabi).find(
          (key) => state.syllabi[key].id === action.payload.id,
        )
        if (eventID) {
          state.syllabiStatus[eventID] = 'fulfilled'
          state.syllabi[eventID] = action.payload
        }
      })
      .addCase(generateSyllabusWithAI.fulfilled, (state, action) => {
        state.syllabusGenerations[action.meta.arg.eventID] = {
          correlationID: action.payload.correlationID,
          status: 'SessionInProgress',
        }
      })
      .addCase(checkSyllabusGenerationStatus.fulfilled, (state, action) => {
        let eventID = Object.keys(state.syllabusGenerations).find(
          (key) =>
            state.syllabusGenerations[key].correlationID ===
            action.payload.correlationID,
        )
        if (eventID) {
          state.syllabusGenerations[eventID] = {
            correlationID: action.payload.correlationID,
            status: action.payload.status,
            result: action.payload.result || undefined,
          }
        }
      })
      // CAPCE Accreditation reducers
      .addCase(fetchCAPCEAccreditation.pending, (state, action) => {
        state.capceAccreditationsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCAPCEAccreditation.rejected, (state, action) => {
        state.capceAccreditationsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCAPCEAccreditation.fulfilled, (state, action) => {
        state.capceAccreditationsStatus[action.meta.arg] = 'fulfilled'
        state.capceAccreditations[action.meta.arg] = action.payload
      })
      .addCase(createCAPCEAccreditation.pending, (state, action) => {
        state.capceAccreditationsStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(createCAPCEAccreditation.rejected, (state, action) => {
        state.capceAccreditationsStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(createCAPCEAccreditation.fulfilled, (state, action) => {
        state.capceAccreditationsStatus[action.meta.arg.eventID] = 'fulfilled'
        state.capceAccreditations[action.meta.arg.eventID] = action.payload
      })
      .addCase(updateCAPCEAccreditation.pending, (state, action) => {
        let eventID = Object.keys(state.capceAccreditations).find(
          (key) => state.capceAccreditations[key].id === action.meta.arg.id,
        )
        if (eventID) {
          state.capceAccreditationsStatus[eventID] = 'pending'
        }
      })
      .addCase(updateCAPCEAccreditation.rejected, (state, action) => {
        let eventID = Object.keys(state.capceAccreditations).find(
          (key) => state.capceAccreditations[key].id === action.meta.arg.id,
        )
        if (eventID) {
          state.capceAccreditationsStatus[eventID] = 'rejected'
        }
      })
      .addCase(updateCAPCEAccreditation.fulfilled, (state, action) => {
        let eventID = Object.keys(state.capceAccreditations).find(
          (key) => state.capceAccreditations[key].id === action.payload.id,
        )
        if (eventID) {
          state.capceAccreditationsStatus[eventID] = 'fulfilled'
          state.capceAccreditations[eventID] = action.payload
        }
      })
      // ANCC Accreditation reducers
      .addCase(fetchANCCAccreditation.pending, (state, action) => {
        state.anccAccreditationsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchANCCAccreditation.rejected, (state, action) => {
        state.anccAccreditationsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchANCCAccreditation.fulfilled, (state, action) => {
        state.anccAccreditationsStatus[action.meta.arg] = 'fulfilled'
        state.anccAccreditations[action.meta.arg] = action.payload
      })
      .addCase(createANCCAccreditation.pending, (state, action) => {
        state.anccAccreditationsStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(createANCCAccreditation.rejected, (state, action) => {
        state.anccAccreditationsStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(createANCCAccreditation.fulfilled, (state, action) => {
        state.anccAccreditationsStatus[action.meta.arg.eventID] = 'fulfilled'
        state.anccAccreditations[action.meta.arg.eventID] = action.payload
      })
      .addCase(updateANCCAccreditation.pending, (state, action) => {
        let eventID = Object.keys(state.anccAccreditations).find(
          (key) => state.anccAccreditations[key].id === action.meta.arg.id,
        )
        if (eventID) {
          state.anccAccreditationsStatus[eventID] = 'pending'
        }
      })
      .addCase(updateANCCAccreditation.rejected, (state, action) => {
        let eventID = Object.keys(state.anccAccreditations).find(
          (key) => state.anccAccreditations[key].id === action.meta.arg.id,
        )
        if (eventID) {
          state.anccAccreditationsStatus[eventID] = 'rejected'
        }
      })
      .addCase(updateANCCAccreditation.fulfilled, (state, action) => {
        let eventID = Object.keys(state.anccAccreditations).find(
          (key) => state.anccAccreditations[key].id === action.payload.id,
        )
        if (eventID) {
          state.anccAccreditationsStatus[eventID] = 'fulfilled'
          state.anccAccreditations[eventID] = action.payload
        }
      })
      // References reducers
      .addCase(fetchReferences.pending, (state, action) => {
        state.referencesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchReferences.rejected, (state, action) => {
        state.referencesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchReferences.fulfilled, (state, action) => {
        state.referencesStatus[action.meta.arg] = 'fulfilled'
        state.references[action.meta.arg] = action.payload
      })
      .addCase(createReferences.pending, (state, action) => {
        state.referencesStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(createReferences.rejected, (state, action) => {
        state.referencesStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(createReferences.fulfilled, (state, action) => {
        state.referencesStatus[action.meta.arg.eventID] = 'fulfilled'
        state.references[action.meta.arg.eventID] = action.payload
      })
      .addCase(updateReferences.pending, (state, action) => {
        state.referencesStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(updateReferences.rejected, (state, action) => {
        state.referencesStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(updateReferences.fulfilled, (state, action) => {
        state.referencesStatus[action.meta.arg.eventID] = 'fulfilled'
        state.references[action.meta.arg.eventID] = action.payload
      })
      // Objectives reducers
      .addCase(fetchObjectives.pending, (state, action) => {
        state.objectivesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchObjectives.rejected, (state, action) => {
        state.objectivesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchObjectives.fulfilled, (state, action) => {
        state.objectivesStatus[action.meta.arg] = 'fulfilled'
        state.objectives[action.meta.arg] = action.payload
      })
      .addCase(createObjectives.pending, (state, action) => {
        state.objectivesStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(createObjectives.rejected, (state, action) => {
        state.objectivesStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(createObjectives.fulfilled, (state, action) => {
        state.objectivesStatus[action.meta.arg.eventID] = 'fulfilled'
        state.objectives[action.meta.arg.eventID] = action.payload
      })
      .addCase(updateObjectives.pending, (state, action) => {
        state.objectivesStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(updateObjectives.rejected, (state, action) => {
        state.objectivesStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(updateObjectives.fulfilled, (state, action) => {
        state.objectivesStatus[action.meta.arg.eventID] = 'fulfilled'
        state.objectives[action.meta.arg.eventID] = action.payload
      })
      // Template reducers
      .addCase(fetchTemplate.pending, (state, action) => {
        state.templatesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchTemplate.rejected, (state, action) => {
        state.templatesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchTemplate.fulfilled, (state, action) => {
        state.templatesStatus[action.meta.arg] = 'fulfilled'
        state.templates[action.meta.arg] = action.payload
      })
      .addCase(assignTemplate.pending, (state, action) => {
        state.templatesStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(assignTemplate.rejected, (state, action) => {
        state.templatesStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(assignTemplate.fulfilled, (state, action) => {
        state.templatesStatus[action.meta.arg.eventID] = 'fulfilled'
        state.templates[action.meta.arg.eventID] = action.payload
      })
      .addCase(updateTemplateAssignment.pending, (state, action) => {
        state.templatesStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(updateTemplateAssignment.rejected, (state, action) => {
        state.templatesStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(updateTemplateAssignment.fulfilled, (state, action) => {
        state.templatesStatus[action.meta.arg.eventID] = 'fulfilled'
        state.templates[action.meta.arg.eventID] = action.payload
      })
      // Instructor reducers
      .addCase(fetchInstructors.pending, (state, action) => {
        state.instructorsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchInstructors.rejected, (state, action) => {
        state.instructorsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchInstructors.fulfilled, (state, action) => {
        state.instructorsStatus[action.meta.arg] = 'fulfilled'
        state.instructors[action.meta.arg] = action.payload
      })
      .addCase(fetchAllEventInstructors.pending, (state) => {
        state.allEventInstructorsStatus = 'pending'
      })
      .addCase(fetchAllEventInstructors.rejected, (state) => {
        state.allEventInstructorsStatus = 'rejected'
      })
      .addCase(fetchAllEventInstructors.fulfilled, (state, action) => {
        state.allEventInstructorsStatus = 'fulfilled'
        action.payload.forEach((row) => {
          state.instructors[row.eventId] = row.instructors
        })
      })
      .addCase(assignInstructors.pending, (state, action) => {
        state.instructorsStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(assignInstructors.rejected, (state, action) => {
        state.instructorsStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(assignInstructors.fulfilled, (state, action) => {
        state.instructorsStatus[action.meta.arg.eventID] = 'fulfilled'
        state.instructors[action.meta.arg.eventID] = action.payload
      })
      .addCase(updateInstructorAssignment.pending, (state, action) => {
        state.instructorsStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(updateInstructorAssignment.rejected, (state, action) => {
        state.instructorsStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(updateInstructorAssignment.fulfilled, (state, action) => {
        state.instructorsStatus[action.meta.arg.eventID] = 'fulfilled'
        state.instructors[action.meta.arg.eventID] = action.payload
      })
      .addCase(fetchAvailableInstructors.pending, (state) => {
        state.availableInstructorsStatus = 'pending'
      })
      .addCase(fetchAvailableInstructors.rejected, (state) => {
        state.availableInstructorsStatus = 'rejected'
      })
      .addCase(fetchAvailableInstructors.fulfilled, (state, action) => {
        state.availableInstructorsStatus = 'fulfilled'
        state.availableInstructors = action.payload
      })
      .addCase(createInstructor.fulfilled, (state, action) => {
        state.availableInstructors = [
          ...state.availableInstructors,
          action.payload,
        ]
      })
      .addCase(updateInstructor.fulfilled, (state, action) => {
        state.availableInstructors = state.availableInstructors.map(
          (instructor) =>
            instructor.id === action.payload.id ? action.payload : instructor,
        )
        Object.keys(state.instructors).forEach((eventID) => {
          state.instructors[eventID] = state.instructors[eventID].map(
            (instructor) =>
              instructor.id === action.payload.id ? action.payload : instructor,
          )
        })
      })
      // Quiz reducers
      .addCase(fetchQuiz.pending, (state, action) => {
        state.quizzesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchQuiz.rejected, (state, action) => {
        state.quizzesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchQuiz.fulfilled, (state, action) => {
        state.quizzesStatus[action.meta.arg] = 'fulfilled'
        state.quizzes[action.meta.arg] = action.payload
      })
      .addCase(createQuiz.pending, (state, action) => {
        state.quizzesStatus[action.meta.arg.eventID] = 'pending'
      })
      .addCase(createQuiz.rejected, (state, action) => {
        state.quizzesStatus[action.meta.arg.eventID] = 'rejected'
      })
      .addCase(createQuiz.fulfilled, (state, action) => {
        state.quizzesStatus[action.meta.arg.eventID] = 'fulfilled'
        state.quizzes[action.meta.arg.eventID] = action.payload
      })
      .addCase(fetchQuizQuestions.pending, (state, action) => {
        state.quizQuestionsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchQuizQuestions.rejected, (state, action) => {
        state.quizQuestionsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchQuizQuestions.fulfilled, (state, action) => {
        state.quizQuestionsStatus[action.meta.arg] = 'fulfilled'
        state.quizQuestions[action.meta.arg] = action.payload
      })
      // Event Lifecycle reducers
      .addCase(preArchiveEvent.pending, (state) => {
        state.eventLifecycleOperationStatus = 'pending'
        state.eventLifecycleOperationError = null
        state.eventLifecycleOperationSuccess = null
      })
      .addCase(preArchiveEvent.fulfilled, (state, action) => {
        state.eventLifecycleOperationStatus = 'fulfilled'
        state.eventLifecycleOperationSuccess = 'Event moved to pre-archive'
        applyLifecycleTransition(
          state,
          action.payload.eventId,
          action.payload.toState,
        )
      })
      .addCase(preArchiveEvent.rejected, (state, action) => {
        state.eventLifecycleOperationStatus = 'rejected'
        state.eventLifecycleOperationError =
          action.error.message || 'Failed to move event to pre-archive'
      })
      .addCase(reactivateEvent.pending, (state) => {
        state.eventLifecycleOperationStatus = 'pending'
        state.eventLifecycleOperationError = null
        state.eventLifecycleOperationSuccess = null
      })
      .addCase(reactivateEvent.fulfilled, (state, action) => {
        state.eventLifecycleOperationStatus = 'fulfilled'
        state.eventLifecycleOperationSuccess = 'Event reactivated'
        applyLifecycleTransition(
          state,
          action.payload.eventId,
          action.payload.toState,
        )
      })
      .addCase(reactivateEvent.rejected, (state, action) => {
        state.eventLifecycleOperationStatus = 'rejected'
        state.eventLifecycleOperationError =
          action.error.message || 'Failed to reactivate event'
      })
      .addCase(archiveEvent.pending, (state) => {
        state.eventLifecycleOperationStatus = 'pending'
        state.eventLifecycleOperationError = null
        state.eventLifecycleOperationSuccess = null
      })
      .addCase(archiveEvent.fulfilled, (state, action) => {
        state.eventLifecycleOperationStatus = 'fulfilled'
        state.eventLifecycleOperationSuccess = 'Event archived'
        applyLifecycleTransition(
          state,
          action.payload.eventId,
          action.payload.toState,
        )
      })
      .addCase(archiveEvent.rejected, (state, action) => {
        state.eventLifecycleOperationStatus = 'rejected'
        state.eventLifecycleOperationError =
          action.error.message || 'Failed to archive event'
      })
      .addCase(restoreEvent.pending, (state) => {
        state.eventLifecycleOperationStatus = 'pending'
        state.eventLifecycleOperationError = null
        state.eventLifecycleOperationSuccess = null
      })
      .addCase(restoreEvent.fulfilled, (state, action) => {
        state.eventLifecycleOperationStatus = 'fulfilled'
        state.eventLifecycleOperationSuccess = 'Event restored to pre-archive'
        applyLifecycleTransition(state, action.payload.eventId, 'preArchive')
      })
      .addCase(restoreEvent.rejected, (state, action) => {
        state.eventLifecycleOperationStatus = 'rejected'
        state.eventLifecycleOperationError =
          action.error.message || 'Failed to restore event'
      })
      .addCase(deletePlanningEvent.pending, (state) => {
        state.eventLifecycleOperationStatus = 'pending'
        state.eventLifecycleOperationError = null
        state.eventLifecycleOperationSuccess = null
      })
      .addCase(deletePlanningEvent.fulfilled, (state, action) => {
        state.eventLifecycleOperationStatus = 'fulfilled'
        state.eventLifecycleOperationSuccess = 'Event deleted'
        state.events = state.events.filter(
          (e) => e.id !== action.payload.eventId,
        )
        state.planningEvents = state.planningEvents.filter(
          (e) => e.id !== action.payload.eventId,
        )
      })
      .addCase(deletePlanningEvent.rejected, (state, action) => {
        state.eventLifecycleOperationStatus = 'rejected'
        state.eventLifecycleOperationError =
          action.error.message || 'Failed to delete event'
      })
  },
})

export let { clearEventLifecycleOperationStatus } = eventPipelineSlice.actions

// ─── Selectors ────────────────────────────────────────────────────────────────

export let selectEvents = (state: AppRootState) => state.eventPipeline.events
export let selectEventsStatus = (state: AppRootState) =>
  state.eventPipeline.eventsStatus

export let selectEventDetails = (state: AppRootState) =>
  state.eventPipeline.eventDetails
export let selectEventDetailsStatus = (state: AppRootState) =>
  state.eventPipeline.eventDetailsStatus
export let selectEventDetailsByID = (id: string) => (state: AppRootState) =>
  state.eventPipeline.eventDetails[id]
export let selectEventDetailsStatusByID =
  (id: string) => (state: AppRootState) =>
    state.eventPipeline.eventDetailsStatus[id]

export let selectEventCompliances = (state: AppRootState) =>
  state.eventPipeline.eventCompliance
export let selectEventComplianceStatus = (state: AppRootState) =>
  state.eventPipeline.eventComplianceStatus
export let selectEventComplianceByID = (id: string) => (state: AppRootState) =>
  state.eventPipeline.eventCompliance[id]

export let selectPlanningEvents = (state: AppRootState) =>
  state.eventPipeline.planningEvents
export let selectPlanningEventsStatus = (state: AppRootState) =>
  state.eventPipeline.planningEventsStatus
export let selectPlanningEventProgress = (state: AppRootState) =>
  state.eventPipeline.planningEventProgress
export let selectPlanningEventProgressByID =
  (id: string) => (state: AppRootState) =>
    state.eventPipeline.planningEventProgress[id]
export let selectPlanningEventProgressStatus = (state: AppRootState) =>
  state.eventPipeline.planningEventProgressStatus
export let selectPlanningEventProgressSummary = (state: AppRootState) =>
  state.eventPipeline.planningEventProgressSummary

export let selectSyllabi = (state: AppRootState) => state.eventPipeline.syllabi
export let selectSyllabiStatus = (state: AppRootState) =>
  state.eventPipeline.syllabiStatus
export let selectSyllabusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.syllabi[eventID]
export let selectSyllabusStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.syllabiStatus[eventID]
export let selectSyllabusGenerations = (state: AppRootState) =>
  state.eventPipeline.syllabusGenerations
export let selectSyllabusGenerationByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.syllabusGenerations[eventID]

export let selectCAPCEAccreditations = (state: AppRootState) =>
  state.eventPipeline.capceAccreditations
export let selectCAPCEAccreditationsStatus = (state: AppRootState) =>
  state.eventPipeline.capceAccreditationsStatus
export let selectCAPCEAccreditationByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.capceAccreditations[eventID]
export let selectCAPCEAccreditationStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.capceAccreditationsStatus[eventID]

export let selectANCCAccreditations = (state: AppRootState) =>
  state.eventPipeline.anccAccreditations
export let selectANCCAccreditationsStatus = (state: AppRootState) =>
  state.eventPipeline.anccAccreditationsStatus
export let selectANCCAccreditationByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.anccAccreditations[eventID]
export let selectANCCAccreditationStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.anccAccreditationsStatus[eventID]

export let selectReferences = (state: AppRootState) =>
  state.eventPipeline.references
export let selectReferencesStatus = (state: AppRootState) =>
  state.eventPipeline.referencesStatus
export let selectReferencesByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.references[eventID]
export let selectReferencesStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.referencesStatus[eventID]

export let selectObjectives = (state: AppRootState) =>
  state.eventPipeline.objectives
export let selectObjectivesStatus = (state: AppRootState) =>
  state.eventPipeline.objectivesStatus
export let selectObjectivesByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.objectives[eventID]
export let selectObjectivesStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.objectivesStatus[eventID]

export let selectTemplates = (state: AppRootState) =>
  state.eventPipeline.templates
export let selectTemplatesStatus = (state: AppRootState) =>
  state.eventPipeline.templatesStatus
export let selectTemplateByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.templates[eventID]
export let selectTemplateStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.templatesStatus[eventID]

export let selectInstructors = (state: AppRootState) =>
  state.eventPipeline.instructors
export let selectInstructorsStatus = (state: AppRootState) =>
  state.eventPipeline.instructorsStatus
export let selectAllEventInstructorsStatus = (state: AppRootState) =>
  state.eventPipeline.allEventInstructorsStatus
export let selectInstructorsByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.instructors[eventID]
export let selectInstructorsStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.instructorsStatus[eventID]
export let selectAllPlanningInstructors = (state: AppRootState) =>
  Object.values(state.eventPipeline.instructors)
    .flat()
    .filter((i, idx, arr) => arr.findIndex((x) => x.id === i.id) === idx)
export let selectAvailableInstructors = (state: AppRootState) =>
  state.eventPipeline.availableInstructors
export let selectAvailableInstructorsStatus = (state: AppRootState) =>
  state.eventPipeline.availableInstructorsStatus

export let selectQuizzes = (state: AppRootState) => state.eventPipeline.quizzes
export let selectQuizzesStatus = (state: AppRootState) =>
  state.eventPipeline.quizzesStatus
export let selectQuizByEventID = (eventID: string) => (state: AppRootState) =>
  state.eventPipeline.quizzes[eventID]
export let selectQuizStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.eventPipeline.quizzesStatus[eventID]

export let selectQuizQuestions = (state: AppRootState) =>
  state.eventPipeline.quizQuestions
export let selectQuizQuestionsStatus = (state: AppRootState) =>
  state.eventPipeline.quizQuestionsStatus
export let selectQuizQuestionsByQuizID =
  (quizID: string) => (state: AppRootState) =>
    state.eventPipeline.quizQuestions[quizID]
export let selectQuizQuestionsStatusByQuizID =
  (quizID: string) => (state: AppRootState) =>
    state.eventPipeline.quizQuestionsStatus[quizID]

export let selectEventLifecycleOperationStatus = (state: AppRootState) =>
  state.eventPipeline.eventLifecycleOperationStatus
export let selectEventLifecycleOperationError = (state: AppRootState) =>
  state.eventPipeline.eventLifecycleOperationError
export let selectEventLifecycleOperationSuccess = (state: AppRootState) =>
  state.eventPipeline.eventLifecycleOperationSuccess

export default eventPipelineSlice.reducer
